import type { ParsedUrlQuery } from 'querystring';
import { useEffect } from 'react';
import type ExploreEventData from '@alltrails/explore-service/types/ExploreEventData';
import useMap from '@alltrails/maps/hooks/useMap';
import { WAYPOINT_ZOOM } from '@alltrails/maps/utils/customRoutes/constants';
import { useApplyWaypointFocus, useWaypointFocusLatLng } from '@/hooks/useWaypointFocus';
import getInitialMapPosition from '@/utils/getInitialMapPosition';
import getSearchParamsMapPosition from '@/utils/getSearchParamsMapPosition';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setInitialMapBounds, setInitialMapCenter, setIsReturningToExplore, setReturningBounds } from '@/utils/slices/exploreSlice';

type Args = {
  hasInitialNaturalLanguageQuery?: boolean;
  searchParams: ParsedUrlQuery;
  useExistingPosition?: boolean;
  userLocation?: { lat: number; lng: number };
};

const useInitialMapPosition = ({ hasInitialNaturalLanguageQuery, searchParams, useExistingPosition, userLocation }: Args) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const { locationResult, initialMapBounds, initialMapCenter, isReturningToExplore, returningBounds } = useAppSelector(state => ({
    locationResult: state.exploreService.locationResult,
    initialMapBounds: state.explore.initialMapBounds,
    initialMapCenter: state.explore.initialMapCenter,
    isReturningToExplore: state.explore.isReturningToExplore,
    returningBounds: state.explore.returningBounds
  }));

  const waypointPosition = useWaypointFocusLatLng();
  useApplyWaypointFocus(waypointPosition);

  useEffect(() => {
    if (waypointPosition) {
      dispatch(setInitialMapCenter([waypointPosition.lat, waypointPosition.lng, WAYPOINT_ZOOM]));
      return;
    }

    if (useExistingPosition && (initialMapBounds || initialMapCenter)) {
      return;
    }

    // When navigating back via useNavigateBack, use the stored returning bounds
    // to restore the map to where the user was before clicking into a detail page.
    // Only used when isReturningToExplore is explicitly set by useNavigateBack.
    if (isReturningToExplore && returningBounds) {
      dispatch(setIsReturningToExplore(false));
      dispatch(setInitialMapBounds(returningBounds));
      dispatch(setReturningBounds(undefined));
      if (map) {
        map.fitBounds(
          [
            [returningBounds.longitudeTopLeft, returningBounds.latitudeBottomRight],
            [returningBounds.longitudeBottomRight, returningBounds.latitudeTopLeft]
          ],
          undefined,
          locationResult ? { preserveLocationResult: true } : {}
        );
      }
      return;
    }

    function setInitialPosition() {
      // On browser back/forward, Next.js restores this page from its client-side cache, so the
      // `searchParams` prop reflects the original server render rather than the restored history
      // entry. Prefer the live URL's position when it has one so the map returns to where the
      // user actually was.
      const windowSearchParams = Object.fromEntries(new URLSearchParams(window.location.search));
      const windowPosition = getSearchParamsMapPosition(windowSearchParams);
      const { bounds, center } = getInitialMapPosition(
        windowPosition.bounds || windowPosition.center ? windowSearchParams : searchParams,
        userLocation
      );
      if (bounds) {
        dispatch(setInitialMapBounds(bounds));
        if (map && !hasInitialNaturalLanguageQuery) {
          // map may already be initialized if navigating from another explore sub route
          let eventData: ExploreEventData = {};
          if (locationResult) {
            eventData = { preserveLocationResult: true };
          }
          map.fitBounds(
            [
              [bounds.longitudeTopLeft, bounds.latitudeBottomRight],
              [bounds.longitudeBottomRight, bounds.latitudeTopLeft]
            ],
            undefined,
            eventData
          );
        }
      } else if (center) {
        dispatch(setInitialMapCenter(center));
      }
    }
    setInitialPosition();
    // Set initial position on initial render only
  }, []);
};

export default useInitialMapPosition;
